import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import Client from './Client';
import Config from './ClientConfig';


export const CLAIM_REACTIVATION_REASONS = [
  { value: 'order_not_synced', label: 'Order was not synchronized' },
  { value: 'cancelled_order_repair', label: 'Cancelled order repair' },
  { value: 'data_reconciliation', label: 'Data reconciliation' },
  { value: 'manual_correction', label: 'Manual correction' },
];

function reasonLabel(reasonCode) {
  return CLAIM_REACTIVATION_REASONS.find(
    (reason) => reason.value === reasonCode,
  )?.label || reasonCode;
}

function responseErrorMessage(response, body) {
  switch (body?.code) {
    case 'active_order_conflict': {
      const orders = body.linkedOrderIrefs || [];
      return orders.length
        ? `The claim is still used by active order(s): ${orders.join(', ')}. Resolve those orders before recovering the claim.`
        : 'The claim is still used by an active order. Resolve that order before recovering the claim.';
    }
    case 'stale_claim_state':
      return 'The claim changed after it was loaded. Its latest data has been refreshed; review it before trying again.';
    case 'claim_not_found':
      return 'This promotion claim no longer exists.';
    case 'invalid_claim_state':
      return 'This claim is not in a state that can be recovered. Refresh its latest data before continuing.';
    case 'invalid_reason_code':
      return 'Select a valid recovery reason.';
    case 'invalid_note':
      return 'The note must contain no more than 500 characters.';
    case 'claim_reactivation_failed':
      return 'The claim was not changed because the backend could not complete and audit the operation.';
    default:
      if (response?.status === 403) {
        return 'Your account is not authorized to recover promotion claims.';
      }
      return body?.message || 'The promotion claim could not be recovered.';
  }
}

function PromotionClaimRecoveryPanel({
  claim,
  onCancel,
  onRecovered,
  onSubmittingChange,
}) {
  const clientRef = useRef(null);
  const abortControllerRef = useRef(null);
  if (!clientRef.current) clientRef.current = new Client('none');

  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [verified, setVerified] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setReasonCode('');
    setNote('');
    setVerified(false);
    setConfirmOpen(false);
    setError('');
  }, [claim?.iref]);

  useEffect(() => () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
  }, []);

  if (!claim) {
    return (
      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ borderRadius: '12px' }}>
        <AlertTitle>Promotion claim recovery</AlertTitle>
        Select <strong>Recover</strong> on an eligible claim after investigating its linked order.
      </Alert>
    );
  }

  const canReview = Boolean(reasonCode) && verified && !submitting;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (canReview) {
      setError('');
      setConfirmOpen(true);
    }
  };

  const closeConfirmation = () => {
    if (!submitting) setConfirmOpen(false);
  };

  const recoverClaim = async () => {
    if (!confirmOpen || submitting || !reasonCode) return;

    const controller = typeof AbortController !== 'undefined'
      ? new AbortController()
      : null;
    abortControllerRef.current = controller;
    setSubmitting(true);
    setError('');
    onSubmittingChange(true);

    const requestOptions = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${clientRef.current.getAuthToken()}`,
      },
      body: JSON.stringify({
        reasonCode,
        expectedStatus: Number(claim.status),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    };
    if (controller) requestOptions.signal = controller.signal;

    try {
      const response = await fetch(
        `${clientRef.current.prefix}${Config.API_URL_PROMOTION_CLAIMS}${claim.iref}/reactivate/`,
        requestOptions,
      );
      let body = {};
      try {
        body = await response.json();
      } catch (_error) {
        body = {};
      }

      if (!response.ok || body.status !== 'SUCCESS') {
        setConfirmOpen(false);
        setVerified(false);
        setError(responseErrorMessage(response, body));
        if (body?.code === 'stale_claim_state') onRecovered(null);
        return;
      }

      setConfirmOpen(false);
      setVerified(false);
      onRecovered(body);
    } catch (requestError) {
      if (requestError?.name !== 'AbortError') {
        setConfirmOpen(false);
        setVerified(false);
        setError('The backend could not be reached. No successful change was confirmed.');
      }
    } finally {
      setSubmitting(false);
      onSubmittingChange(false);
      abortControllerRef.current = null;
    }
  };

  const promotion = claim.promotion || {};
  return (
    <Box sx={{ p: 1.25, borderRadius: '14px', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
      <Stack spacing={1.25}>
        <Box>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#92400e', fontWeight: 950 }}>
                Promotion claim recovery
              </Typography>
              <Typography sx={{ color: '#78716c', fontSize: '0.72rem' }}>
                Claim #{claim.iref} · {promotion.name || 'Unnamed promotion'}
              </Typography>
            </Box>
            <Button
              type="button"
              size="small"
              onClick={onCancel}
              disabled={submitting}
              sx={{ minWidth: 0, textTransform: 'none', fontWeight: 800 }}
            >
              Cancel
            </Button>
          </Stack>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.5 }}>
            <Chip size="small" label={claim.statusLabel || `Status ${claim.status}`} />
            {claim.linkedOrders?.map((orderLink) => (
              <Chip key={orderLink.iref} size="small" variant="outlined" label={`Order #${orderLink.iref}`} />
            ))}
          </Stack>
        </Box>

        <Alert severity="warning" sx={{ py: 0.25 }}>
          Reactivation makes this claim available again. Every attempt is audited.
        </Alert>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={1}>
            <TextField
              select
              label="Recovery reason"
              size="small"
              value={reasonCode}
              onChange={(event) => {
                setReasonCode(event.target.value);
                setVerified(false);
                setError('');
              }}
              required
              fullWidth
              disabled={submitting}
            >
              {CLAIM_REACTIVATION_REASONS.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Operational note"
              size="small"
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
                setVerified(false);
                setError('');
              }}
              helperText={`${note.length}/500 characters · stored in the audit log`}
              inputProps={{ maxLength: 500 }}
              multiline
              minRows={2}
              fullWidth
              disabled={submitting}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={verified}
                  onChange={(event) => {
                    setVerified(event.target.checked);
                    setError('');
                  }}
                  disabled={submitting}
                />
              )}
              label={<Typography sx={{ fontSize: '0.76rem' }}>I verified that this claim should be available again.</Typography>}
            />
            <Button
              type="submit"
              variant="contained"
              color="warning"
              size="small"
              startIcon={<RestoreIcon />}
              disabled={!canReview}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 900 }}
            >
              Review recovery
            </Button>
          </Stack>
        </Box>

        {error ? (
          <Alert severity="error">
            <AlertTitle>Claim was not changed</AlertTitle>
            {error}
          </Alert>
        ) : null}
      </Stack>

      <Dialog open={confirmOpen} onClose={closeConfirmation} maxWidth="sm" fullWidth>
        <DialogTitle>Recover promotion claim #{claim.iref}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              This changes the claim to Claimed and clears its consumption time. The backend will reject it if an active order still owns the claim or its status changed.
            </Alert>
            <Box>
              <Typography variant="caption" color="text.secondary">Current claim</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                #{claim.iref} · {claim.statusLabel || `Status ${claim.status}`} · {promotion.name || 'Unnamed promotion'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Reason</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{reasonLabel(reasonCode)}</Typography>
            </Box>
            {note.trim() ? (
              <Box>
                <Typography variant="caption" color="text.secondary">Note</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{note.trim()}</Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={closeConfirmation} disabled={submitting}>Cancel</Button>
          <Button
            type="button"
            variant="contained"
            color="warning"
            startIcon={submitting ? <CircularProgress size={16} /> : <RestoreIcon />}
            onClick={recoverClaim}
            disabled={submitting}
          >
            {submitting ? 'Recovering…' : 'Confirm recovery'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PromotionClaimRecoveryPanel;
